import {
  getFileExtensionFromFilename,
  getNameAbbreviation,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../Button";
import { Chip } from "../Chip";
import { DropdownMenu } from "../DropdownMenu";
import { getIconVariantFromFileExtension, Icon } from "../Icon";
import styles from "./Message.module.scss";
import type { MessageFileType, MessageProps } from "./types";

const cx = classNames.bind(styles);

const File = ({
  file,
  onClick,
  className,
}: {
  file: MessageFileType;
  onClick: (file: MessageFileType) => void;
  className?: string;
}) => {
  const fileSegments = file.file_name.split(".");
  const fileName = fileSegments[0];
  const fileExtension = fileSegments[1];

  return (
    <div
      className={cx("file-container", className)}
      onClick={() => onClick(file)}
    >
      <Icon
        variant={getIconVariantFromFileExtension(
          getFileExtensionFromFilename(fileName)
        )}
        size={22}
        style={{ marginRight: "8px" }}
      />
      <p className={cx("file-text", "file-name")}>{fileName}</p>
      <p
        className={cx("file-text")}
      >{`${fileExtension ? `.${fileExtension}` : ``}`}</p>
    </div>
  );
};

const Message = ({
  name,
  meta,
  textContent,
  filesDisplay,
  files,
  fileOnClick,
  actions,
  action,
  hasMessageBefore = false,
  hasMessageAfter = false,
  className,
  style,
  as: asProp,
  ...restProps
}: MessageProps) => {
  const Tag = asProp ?? "div";

  const showThread = hasMessageBefore || hasMessageAfter;
  const isIntermediateMessageInThread =
    showThread && hasMessageBefore && hasMessageAfter;
  const isLastMessageInThread =
    showThread && hasMessageBefore && !hasMessageAfter;

  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  return (
    <Tag
      className={cx("container", className)}
      style={style}
      {...restProps}
    >
      <div
        className={cx(
          "thread-container",
          isIntermediateMessageInThread && "intermediate-message",
          isLastMessageInThread && "last-message"
        )}
      >
        <div
          className={cx(
            showThread && "thread",
            isIntermediateMessageInThread && "intermediate-message",
            isLastMessageInThread && "last-message"
          )}
        >
          {isIntermediateMessageInThread || isLastMessageInThread ? (
            <div
              className={cx("branch", isLastMessageInThread && "last-message")}
            ></div>
          ) : null}
        </div>
      </div>
      <div className={cx("content-container")}>
        <div className={cx("message-header-container")}>
          <div className={cx("user-badge")}>
            <Chip
              text={getNameAbbreviation(name) ?? ""}
              shapeVariant="circle"
            />
          </div>
          <div className={cx("message-header-text-content")}>
            <p className={cx("name")}>{name}</p>
            <p className={cx("meta")}>{meta}</p>
          </div>
        </div>
        <div
          className={cx(
            "message-body-container",
            isIntermediateMessageInThread && "intermediate-message",
            isLastMessageInThread && "last-message"
          )}
        >
          <div className={cx("text-content-container")}>
            <p className={cx("text-content")}>{textContent}</p>
          </div>
          {filesDisplay ? (
            filesDisplay
          ) : files && fileOnClick ? (
            <div className={cx("files-container")}>
              {files.map((file) => {
                return (
                  <File
                    key={file.file_id}
                    file={file}
                    className={cx(className)}
                    onClick={fileOnClick}
                  />
                );
              })}
            </div>
          ) : null}
        </div>
      </div>
      <div className={cx("actions-container")}>
        <div
          className={cx("actions", isActionsMenuOpen && "always-visible")}
          // Do not trigger onClick events for the Message component
          onClick={(e) => e.stopPropagation()}
        >
          {actions ? (
            <DropdownMenu
              options={actions}
              rootProps={{ onOpenChange: setIsActionsMenuOpen }}
            >
              <Button
                variant="outline"
                icon="dots-three-vertical"
                size="xx-small"
              />
            </DropdownMenu>
          ) : action ? (
            <Button
              {...action}
              size="xx-small"
            />
          ) : null}
        </div>
      </div>
    </Tag>
  );
};

export default Message;
